import { useCallback, useEffect, useState } from "react";
import {
  collection,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/Card";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { EmptyState } from "@/components/ui/EmptyState";

const PAGE_SIZE = 20;

interface CatalogIngredient {
  name: string;
  quantity: number;
  unit: string;
  category: string;
  optional: boolean;
}

interface CatalogMeal {
  id: string;
  name: string;
  description: string;
  genre: string[];
  dietary_tags: string[];
  prep_time_minutes: number;
  servings: number;
  calories_per_serving: number | null;
  ingredients: CatalogIngredient[];
  hero_image_url: string | null;
  hero_image_attribution: string | null;
  source: string;
  review_status: string;
}

function toMeal(snap: QueryDocumentSnapshot<DocumentData>): CatalogMeal {
  const data = snap.data();
  return {
    id: snap.id,
    name: (data.name as string) ?? "",
    description: (data.description as string) ?? "",
    genre: (data.genre as string[]) ?? [],
    dietary_tags: (data.dietary_tags as string[]) ?? [],
    prep_time_minutes: (data.prep_time_minutes as number) ?? 0,
    servings: (data.servings as number) ?? 0,
    calories_per_serving: (data.calories_per_serving as number | null) ?? null,
    ingredients: (data.ingredients as CatalogIngredient[]) ?? [],
    hero_image_url: (data.hero_image_url as string | null) ?? null,
    hero_image_attribution: (data.hero_image_attribution as string | null) ?? null,
    source: (data.source as string) ?? "",
    review_status: (data.review_status as string) ?? "pending",
  };
}

export function AdminCookbookPage() {
  const { user, loading: authLoading } = useAuthContext();

  const [meals, setMeals] = useState<CatalogMeal[]>([]);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (after: QueryDocumentSnapshot<DocumentData> | null) => {
      setLoading(true);
      setErr(null);
      try {
        const constraints: QueryConstraint[] = [
          where("review_status", "==", "pending"),
          orderBy("created_at", "desc"),
        ];
        if (after) constraints.push(startAfter(after));
        constraints.push(fbLimit(PAGE_SIZE));
        const q = query(collection(db, "meal_catalog"), ...constraints);
        const snap = await getDocs(q);
        const next = snap.docs.map(toMeal);
        setMeals((prev) => (after ? [...prev, ...next] : next));
        setCursor(snap.docs[snap.docs.length - 1] ?? null);
        setHasMore(snap.docs.length === PAGE_SIZE);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Failed to load meals.");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (user) fetchPage(null);
  }, [user, fetchPage]);

  const callApprove = useCallback(async (mealId: string) => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "approveCatalogMeal");
    await fn({ mealId });
  }, []);

  const callReject = useCallback(async (mealId: string) => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "rejectCatalogMeal");
    await fn({ mealId });
  }, []);

  const runOne = useCallback(
    async (mealId: string, action: "approve" | "reject") => {
      setBusy((b) => new Set(b).add(mealId));
      try {
        if (action === "approve") await callApprove(mealId);
        else await callReject(mealId);
        setMeals((prev) => prev.filter((m) => m.id !== mealId));
        setSelected((sel) => {
          const next = new Set(sel);
          next.delete(mealId);
          return next;
        });
      } catch (e) {
        setErr(e instanceof Error ? e.message : `Failed to ${action} ${mealId}.`);
      } finally {
        setBusy((b) => {
          const next = new Set(b);
          next.delete(mealId);
          return next;
        });
      }
    },
    [callApprove, callReject],
  );

  const runBulk = useCallback(
    async (action: "approve" | "reject") => {
      const ids = Array.from(selected);
      if (ids.length === 0) return;
      const confirmed =
        action === "approve" || window.confirm(`Reject (delete) ${ids.length} meals? This cannot be undone.`);
      if (!confirmed) return;
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await runOne(id, action);
      }
    },
    [selected, runOne],
  );

  const toggleSelect = useCallback((mealId: string) => {
    setSelected((sel) => {
      const next = new Set(sel);
      if (next.has(mealId)) next.delete(mealId);
      else next.add(mealId);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelected((sel) => {
      const next = new Set(sel);
      meals.forEach((m) => next.add(m.id));
      return next;
    });
  }, [meals]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        <p>Sign in to use this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <SectionHeader title="Cookbook · Pending Review" />

      <Card>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-gray-600 dark:text-gray-300">
            {selected.size} selected · {meals.length} loaded
          </span>
          <div className="flex-1" />
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50"
            onClick={selectAllVisible}
            disabled={meals.length === 0}
          >
            Select all visible
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50"
            onClick={clearSelection}
            disabled={selected.size === 0}
          >
            Clear
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white disabled:opacity-50"
            onClick={() => runBulk("approve")}
            disabled={selected.size === 0}
          >
            Approve selected
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg bg-rose-600 text-white disabled:opacity-50"
            onClick={() => runBulk("reject")}
            disabled={selected.size === 0}
          >
            Reject selected
          </button>
        </div>
      </Card>

      {err && (
        <Card className="border-rose-300/70">
          <p className="text-sm text-rose-700 dark:text-rose-300">{err}</p>
        </Card>
      )}

      {meals.length === 0 && !loading && (
        <EmptyState
          icon="🍽"
          title="Nothing pending"
          subtitle="The seed run hasn't produced any pending meals, or you've reviewed them all."
        />
      )}

      <div className="grid gap-3">
        {meals.map((meal) => (
          <MealReviewRow
            key={meal.id}
            meal={meal}
            checked={selected.has(meal.id)}
            busy={busy.has(meal.id)}
            onToggle={() => toggleSelect(meal.id)}
            onApprove={() => runOne(meal.id, "approve")}
            onReject={() => runOne(meal.id, "reject")}
          />
        ))}
      </div>

      <div className="flex justify-center pt-2">
        {hasMore && (
          <button
            type="button"
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50"
            disabled={loading}
            onClick={() => fetchPage(cursor)}
          >
            {loading ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}

interface MealReviewRowProps {
  meal: CatalogMeal;
  checked: boolean;
  busy: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReject: () => void;
}

function MealReviewRow({ meal, checked, busy, onToggle, onApprove, onReject }: MealReviewRowProps) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <div className="flex gap-3">
        <input
          type="checkbox"
          className="mt-1"
          checked={checked}
          onChange={onToggle}
          aria-label={`Select ${meal.name}`}
        />
        {meal.hero_image_url ? (
          <img
            src={meal.hero_image_url}
            alt=""
            className="w-24 h-24 rounded-xl object-cover bg-gray-100 dark:bg-gray-700"
            loading="lazy"
          />
        ) : (
          <div className="w-24 h-24 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-400">
            no image
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-semibold truncate">{meal.name}</h3>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {meal.prep_time_minutes}m · {meal.servings} servings
              {meal.calories_per_serving != null && ` · ~${meal.calories_per_serving} cal`}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{meal.description}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {meal.genre.map((g) => (
              <span
                key={g}
                className="text-[11px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200"
              >
                {g}
              </span>
            ))}
            {meal.dietary_tags.map((t) => (
              <span
                key={t}
                className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200"
              >
                {t}
              </span>
            ))}
          </div>
          <button
            type="button"
            className="text-xs text-blue-600 dark:text-blue-300 mt-2"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? "Hide ingredients" : `Show ingredients (${meal.ingredients.length})`}
          </button>
          {open && (
            <ul className="mt-1 text-xs text-gray-600 dark:text-gray-300 space-y-0.5">
              {meal.ingredients.map((i, idx) => (
                <li key={idx}>
                  {i.quantity} {i.unit} {i.name}
                  {i.optional ? " (optional)" : ""}
                  <span className="text-gray-400"> · {i.category}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white disabled:opacity-50 text-sm"
            onClick={onApprove}
            disabled={busy}
          >
            Approve
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg bg-rose-600 text-white disabled:opacity-50 text-sm"
            onClick={onReject}
            disabled={busy}
          >
            Reject
          </button>
        </div>
      </div>
    </Card>
  );
}
